/**
 * Measurement in and out of the cut list.
 *
 * Everything the module computes with is a millimetre — a plain number, never a
 * tagged value — because the packing engines run millions of comparisons and a
 * boxed unit would cost more than the arithmetic. Units exist at the two edges:
 * reading what a user typed, and writing what a user reads.
 *
 * The module's display unit is deliberately independent of the project's. A
 * workshop that models in inches still cuts in millimetres, and 21.2 of the
 * functional spec makes that a requirement rather than a convenience.
 */

export type LengthUnitId = "mm" | "cm" | "m" | "in" | "ft" | "yd";
export type MassUnitId = "kg" | "lb";
export type UnitSystem = "metric" | "imperial";

/** How many millimetres one of each unit is worth. */
const MILLIMETRES_PER: Record<LengthUnitId, number> = {
  mm: 1,
  cm: 10,
  m: 1_000,
  in: 25.4,
  ft: 304.8,
  yd: 914.4,
};

const GRAMS_PER: Record<MassUnitId, number> = {
  kg: 1_000,
  lb: 453.59237,
};

export const LENGTH_UNIT_LABELS: Record<LengthUnitId, string> = {
  mm: "mm",
  cm: "cm",
  m: "m",
  in: '"',
  ft: "'",
  yd: "yd",
};

export const MASS_UNIT_LABELS: Record<MassUnitId, string> = { kg: "kg", lb: "lb" };

/**
 * Every spelling a dimension field is expected to accept. Longest match wins,
 * so "mm" is never read as "m".
 */
const LENGTH_ALIASES: Array<[string, LengthUnitId]> = [
  ["millimeters", "mm"], ["millimetres", "mm"], ["milimetros", "mm"], ["mm", "mm"],
  ["centimeters", "cm"], ["centimetres", "cm"], ["centimetros", "cm"], ["cm", "cm"],
  ["meters", "m"], ["metres", "m"], ["metros", "m"], ["m", "m"],
  ["inches", "in"], ["inch", "in"], ["pol", "in"], ["in", "in"], ['"', "in"], ["''", "in"],
  ["feet", "ft"], ["foot", "ft"], ["pes", "ft"], ["ft", "ft"], ["'", "ft"],
  ["yards", "yd"], ["yard", "yd"], ["jardas", "yd"], ["yd", "yd"],
];

export function unitSystemOf(unit: LengthUnitId): UnitSystem {
  return unit === "in" || unit === "ft" || unit === "yd" ? "imperial" : "metric";
}

export function toMillimetres(value: number, unit: LengthUnitId): number {
  return value * MILLIMETRES_PER[unit];
}

export function fromMillimetres(millimetres: number, unit: LengthUnitId): number {
  return millimetres / MILLIMETRES_PER[unit];
}

export function massToGrams(value: number, unit: MassUnitId): number {
  return value * GRAMS_PER[unit];
}

export function massFromGrams(grams: number, unit: MassUnitId): number {
  return grams / GRAMS_PER[unit];
}

/* -------------------------------------------------------------------------- */
/* Display                                                                     */
/* -------------------------------------------------------------------------- */

export interface LengthFormat {
  unit: LengthUnitId;
  /** Decimal places, or the fraction denominator when notation is fractional. */
  precision: number;
  notation: "decimal" | "fractional";
  /** Whether the unit suffix is appended. */
  suffix: boolean;
  locale: string;
}

export const DEFAULT_LENGTH_FORMAT: LengthFormat = {
  unit: "mm",
  precision: 1,
  notation: "decimal",
  suffix: false,
  locale: "pt-BR",
};

/**
 * A length, written the way the user asked for it.
 *
 * Fractional notation rounds to the nearest 1/denominator and then reduces, so
 * 8/16 prints as 1/2 and 16/16 rolls into the whole number. Feet carry their
 * inches alongside — 6'3" rather than 6.25' — because that is how a cut list is
 * read on a shop floor.
 */
export function formatLength(millimetres: number, format: Partial<LengthFormat> = {}): string {
  const settings = { ...DEFAULT_LENGTH_FORMAT, ...format };
  if (!Number.isFinite(millimetres)) return "—";
  const value = fromMillimetres(millimetres, settings.unit);
  const text = settings.notation === "fractional"
    ? formatFractional(value, settings.unit, Math.max(1, Math.round(settings.precision)))
    : formatDecimal(value, settings.precision, settings.locale);
  if (!settings.suffix) return text;
  if (settings.notation === "fractional" && (settings.unit === "in" || settings.unit === "ft")) return text;
  return `${text} ${LENGTH_UNIT_LABELS[settings.unit]}`;
}

export function formatDecimal(value: number, precision: number, locale = "pt-BR"): string {
  const places = Math.max(0, Math.min(9, Math.round(precision)));
  const rounded = roundTo(value, places);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: places,
  }).format(rounded === 0 ? 0 : rounded);
}

function formatFractional(value: number, unit: LengthUnitId, denominator: number): string {
  if (unit === "ft") {
    const sign = value < 0 ? "-" : "";
    const totalInches = Math.abs(value) * 12;
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches - feet * 12;
    const inchText = formatFractionalInches(inches, denominator);
    if (feet === 0) return `${sign}${inchText}"`;
    return inchText === "0" ? `${sign}${feet}'` : `${sign}${feet}' ${inchText}"`;
  }
  if (unit === "in") return `${formatFractionalInches(value, denominator)}"`;
  // A metric unit asked for fractional notation still gets a readable answer.
  return formatDecimal(value, 2);
}

function formatFractionalInches(value: number, denominator: number): string {
  const sign = value < 0 ? "-" : "";
  const absolute = Math.abs(value);
  const ticks = Math.round(absolute * denominator);
  const whole = Math.floor(ticks / denominator);
  let numerator = ticks - whole * denominator;
  let base = denominator;
  while (numerator > 0 && numerator % 2 === 0 && base % 2 === 0) {
    numerator /= 2;
    base /= 2;
  }
  if (numerator === 0) return `${sign}${whole}`;
  if (whole === 0) return `${sign}${numerator}/${base}`;
  return `${sign}${whole} ${numerator}/${base}`;
}

export function formatMass(grams: number, unit: MassUnitId, locale = "pt-BR"): string {
  if (!Number.isFinite(grams)) return "—";
  return `${formatDecimal(massFromGrams(grams, unit), 3, locale)} ${MASS_UNIT_LABELS[unit]}`;
}

export function formatArea(squareMillimetres: number, locale = "pt-BR"): string {
  return `${formatDecimal(squareMillimetres / 1_000_000, 4, locale)} m²`;
}

export function formatVolume(cubicMillimetres: number, locale = "pt-BR"): string {
  return `${formatDecimal(cubicMillimetres / 1_000_000_000, 6, locale)} m³`;
}

export function formatCurrency(value: number, symbol: string, locale = "pt-BR"): string {
  const amount = formatDecimal(value, 2, locale);
  // Portuguese and Spanish put the symbol first with a space; English joins it.
  return locale.startsWith("en") ? `${symbol}${amount}` : `${symbol} ${amount}`;
}

export function roundTo(value: number, places: number): number {
  const factor = 10 ** places;
  // Nudging by an epsilon proportional to the value keeps 0.615 at two places
  // from landing on 0.61 because of how it is stored in binary.
  const scaled = value * factor;
  const nudged = scaled + Math.sign(scaled) * Math.abs(scaled) * Number.EPSILON * 4;
  return Math.round(nudged) / factor;
}

/* -------------------------------------------------------------------------- */
/* Input                                                                       */
/* -------------------------------------------------------------------------- */

export class LengthInputError extends Error {
  constructor(message: string, readonly input: string) {
    super(message);
    this.name = "LengthInputError";
  }
}

/**
 * Reads what a user typed into a dimension field and answers in millimetres.
 *
 * Accepted, in one expression: a bare number in the field's unit (`1200`), an
 * explicit unit (`1200mm`, `48"`, `4'`), an imperial compound (`4' 6 1/2"`),
 * arithmetic (`1200/3`, `(800+400)/2`), and any mix of the three — the spec
 * asks for it in 17.1 and again in 21.2, and a shop types `2440/2-9` far more
 * often than it types the answer.
 *
 * The grammar is closed: numbers, unit suffixes, `+ - * / ( )` and nothing
 * else. There is no identifier production, so no name can be reached, which is
 * what keeps a pasted value from an imported file harmless.
 */
export function parseLength(input: string, defaultUnit: LengthUnitId = "mm"): number {
  const source = input.trim();
  if (!source) throw new LengthInputError("Informe um valor.", input);
  const imperial = parseImperialCompound(source);
  if (imperial !== null) return imperial;
  const tokens = tokenizeLength(source, input);
  let cursor = 0;

  const peek = () => tokens[cursor];
  const take = () => tokens[cursor++];

  /** value := number unit? — with an implicit sum between adjacent values. */
  const parseValue = (): number => {
    const token = take();
    if (!token) throw new LengthInputError("A expressão terminou antes da hora.", input);
    if (token.kind === "open") {
      const inner = parseSum();
      const close = take();
      if (!close || close.kind !== "close") throw new LengthInputError("Falta fechar um parêntese.", input);
      return inner;
    }
    if (token.kind === "operator" && (token.text === "-" || token.text === "+")) {
      const operand = parseValue();
      return token.text === "-" ? -operand : operand;
    }
    if (token.kind !== "number") throw new LengthInputError(`Não entendi "${token.text}".`, input);
    let magnitude = token.value;
    let unit: LengthUnitId | null = null;
    const next = peek();
    if (next?.kind === "unit") {
      take();
      unit = next.unit;
    }
    // "6 1/2" and "4' 6"" both read as a sum of adjacent measured values.
    const following = peek();
    if (following?.kind === "number" && unit !== null) {
      magnitude = toMillimetres(magnitude, unit);
      return magnitude + parseValue();
    }
    if (unit === null) return toMillimetres(magnitude, defaultUnit);
    return toMillimetres(magnitude, unit);
  };

  const parseProduct = (): number => {
    let left = parseValue();
    for (;;) {
      const token = peek();
      if (token?.kind !== "operator" || (token.text !== "*" && token.text !== "/")) break;
      take();
      const right = parseValue();
      if (token.text === "/") {
        if (Math.abs(right) < 1e-12) throw new LengthInputError("Divisão por zero.", input);
        // A quotient of two lengths is a ratio, so the millimetres cancel and
        // the field's own unit is restored. Dividing by a bare number keeps it.
        left /= right;
      } else {
        left *= right / toMillimetres(1, defaultUnit);
      }
    }
    return left;
  };

  const parseSum = (): number => {
    let left = parseProduct();
    for (;;) {
      const token = peek();
      if (token?.kind !== "operator" || (token.text !== "+" && token.text !== "-")) break;
      take();
      const right = parseProduct();
      left = token.text === "+" ? left + right : left - right;
    }
    return left;
  };

  const result = parseSum();
  if (cursor < tokens.length) throw new LengthInputError("Sobrou conteúdo no fim da expressão.", input);
  if (!Number.isFinite(result)) throw new LengthInputError("O resultado não é um número.", input);
  return result;
}

/** Same grammar, but answers `null` instead of throwing. */
export function tryParseLength(input: string, defaultUnit: LengthUnitId = "mm"): number | null {
  try {
    return parseLength(input, defaultUnit);
  } catch {
    return null;
  }
}

/**
 * The one shape the general grammar cannot read: a compound imperial dimension.
 *
 * `4' 6 1/2"` is three adjacent values where only the outer two carry a mark,
 * and the whole thing means one length. Reading it with the arithmetic grammar
 * would make `6 1/2` a division next to a bare 6. It is recognised here first,
 * and only when a foot or inch mark is actually present — so `2440/2-9` still
 * goes to the general parser and still means 1211.
 */
const IMPERIAL_COMPOUND =
  /^\s*(?:(\d+(?:[.,]\d+)?)\s*')?\s*(?:(\d+(?:[.,]\d+)?)(?![\d./]))?\s*(?:(\d+)\s*\/\s*(\d+))?\s*(")?\s*$/;

function parseImperialCompound(source: string): number | null {
  const normalized = source.replace(/[‘’′]/g, "'").replace(/[“”″]/g, '"');
  const match = IMPERIAL_COMPOUND.exec(normalized);
  if (!match) return null;
  const [, feetText, wholeText, numeratorText, denominatorText, inchMark] = match;
  if (!feetText && !inchMark) return null;
  const number = (text: string | undefined): number =>
    text ? Number(text.replace(",", ".")) : 0;
  const denominator = number(denominatorText);
  const fraction = denominator > 0 ? number(numeratorText) / denominator : 0;
  const inches = number(wholeText) + fraction;
  return toMillimetres(number(feetText), "ft") + toMillimetres(inches, "in");
}

type LengthToken =
  | { kind: "number"; value: number; text: string }
  | { kind: "unit"; unit: LengthUnitId; text: string }
  | { kind: "operator"; text: string }
  | { kind: "open" | "close"; text: string };

function tokenizeLength(source: string, original: string): LengthToken[] {
  const tokens: LengthToken[] = [];
  const normalized = source
    .replace(/ /g, " ")
    .replace(/[‘’′]/g, "'")
    .replace(/[“”″]/g, '"');
  let index = 0;
  while (index < normalized.length) {
    const character = normalized[index]!;
    if (/\s/.test(character)) {
      index += 1;
      continue;
    }
    if (character >= "0" && character <= "9") {
      const start = index;
      while (index < normalized.length && /[0-9]/.test(normalized[index]!)) index += 1;
      // A decimal separator may be either mark; a shop keyboard produces both.
      if (normalized[index] === "." || normalized[index] === ",") {
        const decimalStart = index;
        index += 1;
        while (index < normalized.length && /[0-9]/.test(normalized[index]!)) index += 1;
        if (index === decimalStart + 1) index = decimalStart;
      }
      const text = normalized.slice(start, index);
      tokens.push({ kind: "number", value: Number(text.replace(",", ".")), text });
      continue;
    }
    if (character === "(" || character === ")") {
      tokens.push({ kind: character === "(" ? "open" : "close", text: character });
      index += 1;
      continue;
    }
    if ("+-*/".includes(character)) {
      tokens.push({ kind: "operator", text: character });
      index += 1;
      continue;
    }
    const rest = normalized.slice(index).toLocaleLowerCase("en-US");
    const alias = LENGTH_ALIASES.find(([spelling]) => rest.startsWith(spelling));
    if (alias) {
      tokens.push({ kind: "unit", unit: alias[1], text: alias[0] });
      index += alias[0].length;
      continue;
    }
    throw new LengthInputError(`Caractere inesperado: "${character}".`, original);
  }
  return tokens;
}

/**
 * A list of dimensions written as "18;22;25", the way the material form stores
 * standard thicknesses and lengths. Invalid entries are dropped rather than
 * failing the whole list: a stray semicolon should not cost the other values.
 */
export function parseLengthList(input: string, defaultUnit: LengthUnitId = "mm"): number[] {
  return input
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => tryParseLength(entry, defaultUnit))
    .filter((value): value is number => value !== null && value > 0);
}

/** A list of "2800x2070;2500x1250" pairs, for sheet sizes and bar sections. */
export function parseSizeList(
  input: string,
  defaultUnit: LengthUnitId = "mm",
): Array<{ lengthMm: number; widthMm: number }> {
  return input
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const parts = entry.split(/[x×]/i);
      if (parts.length !== 2) return null;
      const lengthMm = tryParseLength(parts[0]!, defaultUnit);
      const widthMm = tryParseLength(parts[1]!, defaultUnit);
      if (lengthMm === null || widthMm === null || lengthMm <= 0 || widthMm <= 0) return null;
      return { lengthMm, widthMm };
    })
    .filter((size): size is { lengthMm: number; widthMm: number } => size !== null);
}

export function formatLengthList(values: readonly number[], format: Partial<LengthFormat> = {}): string {
  return values.map((value) => formatLength(value, format)).join(";");
}

export function formatSizeList(
  sizes: ReadonlyArray<{ lengthMm: number; widthMm: number }>,
  format: Partial<LengthFormat> = {},
): string {
  return sizes
    .map((size) => `${formatLength(size.lengthMm, format)}x${formatLength(size.widthMm, format)}`)
    .join(";");
}
